/**
 * ExportButton Component
 *
 * Standalone export button for data export across all tables.
 * Always enabled regardless of row selection state - exports all data with current filters.
 *
 * Converted from Dropdown to true Button for better UX (1 click instead of 2).
 *
 * Usage:
 * ```tsx
 * <ExportButton onClick={() => setShowExportModal(true)} />
 * ```
 *
 * @module components/actions
 */

import { ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

export interface ExportButtonProps {
  /** Handler for export button click */
  onClick: () => void;
  /** Disable the button (default: false - always enabled) */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for E2E testing */
  'data-testid'?: string;
}

/**
 * ExportButton Component
 *
 * Always-enabled export button that matches the visual style of action dropdowns.
 * Designed to work independently of row selection state.
 *
 * Now uses true Button component for direct 1-click interaction.
 *
 * @param props - Component props
 * @returns Export button JSX
 *
 * @example
 * ```tsx
 * <ExportButton
 *   onClick={() => setShowExportModal(true)}
 *   data-testid="vulnerability-export-button"
 * />
 * ```
 */
export function ExportButton({
  onClick,
  disabled = false,
  className,
  'data-testid': testId = 'export-button',
}: ExportButtonProps) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="outline"
      size="sm"
      className={cn('rounded-full', className)}
      data-testid={testId}
      aria-label="Export data"
    >
      <ArrowUpOnSquareIcon className="size-4" />
      Export
    </Button>
  );
}
